import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Phone, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTS = ["pendiente", "confirmada", "completada", "cancelada"];
const COLORS = {
  pendiente: "bg-[#F4F0EB] text-[#5C5E5C] border-[#D9CDBC]",
  confirmada: "bg-[#E6F0E8] text-[#3a5a44] border-[#7A9180]",
  completada: "bg-[#1A1C1A] text-[#FAFAFA] border-[#1A1C1A]",
  cancelada: "bg-[#F5E0E0] text-[#7a3a3a] border-[#965D5D]",
};

export default function AppointmentsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/appointments");
      setItems(r.data);
    } catch {
      toast.error("Error cargando citas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/appointments/${id}`, { status });
      toast.success("Estado actualizado");
      setItems(items.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar esta cita?")) return;
    try {
      await api.delete(`/appointments/${id}`);
      setItems(items.filter((it) => it.id !== id));
      toast.success("Cita eliminada");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const waLink = (phone, name) => {
    const clean = String(phone).replace(/\D/g, "");
    const prefixed = clean.startsWith("52") ? clean : `52${clean}`;
    return `https://wa.me/${prefixed}?text=${encodeURIComponent(`Hola ${name}, te saluda la clínica de la Dra. Tania Mendoza. Confirmamos tu cita.`)}`;
  };

  return (
    <div data-testid="appointments-panel">
      <div className="bg-white rounded-2xl border border-[#E6E2DC] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F0EB] text-left text-[10px] tracking-[0.25em] uppercase text-[#5C5E5C]">
            <tr>
              <th className="px-5 py-4">Paciente</th>
              <th className="px-5 py-4">Servicio</th>
              <th className="px-5 py-4">Fecha / Hora</th>
              <th className="px-5 py-4">Estado</th>
              <th className="px-5 py-4">Contacto</th>
              <th className="px-5 py-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E6E2DC]">
            {loading && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-[#5C5E5C]">Cargando...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-[#5C5E5C]" data-testid="appointments-empty">Aún no hay citas agendadas.</td></tr>
            )}
            {items.map((a) => (
              <tr key={a.id} data-testid={`appointment-row-${a.id}`}>
                <td className="px-5 py-4">
                  <div className="font-medium">{a.name}</div>
                  {a.notes && <div className="text-xs text-[#5C5E5C] mt-1">{a.notes}</div>}
                </td>
                <td className="px-5 py-4 text-[#5C5E5C]">{a.service}</td>
                <td className="px-5 py-4">
                  <div className="text-[#1A1C1A]">{a.date}</div>
                  <div className="text-xs text-[#5C5E5C]">{a.time}</div>
                </td>
                <td className="px-5 py-4">
                  <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                    <SelectTrigger className="h-8 text-xs w-36" data-testid={`status-select-${a.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-2 text-xs text-[#5C5E5C]">
                    <a href={`tel:${a.phone}`} className="flex items-center gap-1 hover:text-[#A69076]" title="Llamar"><Phone className="w-3.5 h-3.5" /> {a.phone}</a>
                  </div>
                  {a.email && (
                    <div className="flex items-center gap-1 text-xs text-[#5C5E5C] mt-1">
                      <Mail className="w-3.5 h-3.5" /> {a.email}
                    </div>
                  )}
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-2">
                    <a href={waLink(a.phone, a.name)} target="_blank" rel="noreferrer" data-testid={`wa-${a.id}`}>
                      <Button size="sm" variant="outline" className="h-8 text-xs rounded-full border-[#7A9180] text-[#3a5a44] hover:bg-[#E6F0E8]">
                        <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp
                      </Button>
                    </a>
                    <button onClick={() => remove(a.id)} className="p-2 text-[#965D5D] hover:bg-[#F5E0E0] rounded-lg" data-testid={`delete-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
